import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import api from '../../services/api';
import {
  Client,
  PublicWidgetPayload,
  PublicWidgetReview,
  WidgetConfigResponse,
  WidgetStyleConfig,
} from '../../types';
import WidgetPreview from './WidgetPreview';
import './WidgetConfigPage.css';

const DEFAULT_CONFIG: WidgetStyleConfig = {
  title: 'Customer Reviews',
  layout: 'carousel',
  max_reviews: 10,
  show_rating: true,
  show_date: true,
  show_comment: true,
  show_name: true,
  show_average: true,
  autoplay: true,
  autoplay_interval: 5000,
  background_color: '#ffffff',
  card_background_color: '#ffffff',
  text_color: '#4a5568',
  name_color: '#1a202c',
  star_color: '#FFB400',
  accent_color: '#FF6600',
  border_color: '#e2e8f0',
  font_family: 'Arial, Helvetica, sans-serif',
  font_size: 14,
  border_radius: 12,
};

const FONT_OPTIONS = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Open Sans', value: 'Open Sans, sans-serif' },
  { label: 'Lato', value: 'Lato, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
];

const SAMPLE_REVIEWS: PublicWidgetReview[] = [
  {
    id: -1,
    name: 'Sarah M.',
    initial: 'S',
    rating: 9.5,
    comment: 'Excellent service from start to finish. The team was professional, on time, and went above and beyond.',
    date: '2026-09-12',
  },
  {
    id: -2,
    name: 'James K.',
    initial: 'J',
    rating: 8.5,
    comment: 'Very happy with the results. Communication was clear and the quality of work was outstanding.',
    date: '2026-09-08',
  },
  {
    id: -3,
    name: 'Priya R.',
    initial: 'P',
    rating: 10,
    comment: 'I would definitely recommend them. Friendly staff and a seamless experience overall.',
    date: '2026-09-02',
  },
];

const COLOR_FIELDS: Array<{ key: keyof WidgetStyleConfig; label: string }> = [
  { key: 'background_color', label: 'Background color' },
  { key: 'card_background_color', label: 'Card background' },
  { key: 'text_color', label: 'Text color' },
  { key: 'name_color', label: 'Name color' },
  { key: 'star_color', label: 'Star color' },
  { key: 'accent_color', label: 'Accent color' },
  { key: 'border_color', label: 'Border color' },
];

function widgetBaseUrl(): string {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return apiUrl.replace(/\/api\/?$/, '');
}

const WidgetConfigPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { selectedClientId } = useClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [uniqId, setUniqId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState<WidgetStyleConfig>(DEFAULT_CONFIG);
  const [reviews, setReviews] = useState<PublicWidgetReview[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const init = async () => {
      if (isAdmin()) {
        try {
          const response = await api.get<Client[]>('/clients');
          setClients(response.data);
          if (response.data.length > 0) {
            setClientId(response.data[0].id);
          } else {
            setLoading(false);
          }
        } catch (error) {
          console.error('Failed to fetch companies', error);
          setLoading(false);
        }
      } else if (selectedClientId) {
        setClientId(selectedClientId);
      } else {
        setLoading(false);
      }
    };
    init();
  }, [isAdmin, selectedClientId]);

  useEffect(() => {
    if (!clientId) return;

    const load = async () => {
      try {
        setLoading(true);
        setMessage(null);
        const response = await api.get<WidgetConfigResponse>('/widget-config', {
          params: { client_id: clientId },
        });
        setEnabled(response.data.enabled);
        setConfig({ ...DEFAULT_CONFIG, ...response.data.config });
        setUniqId(response.data.client.uniq_id);
        setCompanyName(response.data.client.client_name);

        try {
          const publicData = await api.get<PublicWidgetPayload>(`/public/widget/${response.data.client.uniq_id}`);
          setReviews(publicData.data.reviews || []);
          setAverageRating(publicData.data.average_rating || 0);
          setTotal(publicData.data.total || 0);
        } catch {
          setReviews([]);
          setAverageRating(0);
          setTotal(0);
        }
      } catch (error: any) {
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to load widget configuration',
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [clientId]);

  const embedCode = useMemo(() => {
    if (!uniqId) return '';
    const src = `${widgetBaseUrl()}/widget.js`;
    return `<div class="sitedudes-reviews" data-sitedudes-widget="${uniqId}"></div>\n<script src="${src}" async></script>`;
  }, [uniqId]);

  const updateConfig = <K extends keyof WidgetStyleConfig>(key: K, value: WidgetStyleConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!clientId) return;
    try {
      setSaving(true);
      setMessage(null);
      const response = await api.put<WidgetConfigResponse>('/widget-config', {
        client_id: clientId,
        enabled,
        config,
      });
      setEnabled(response.data.enabled);
      setConfig({ ...DEFAULT_CONFIG, ...response.data.config });
      setMessage({ type: 'success', text: 'Widget settings saved.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save widget settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Could not copy embed code. Please copy it manually.' });
    }
  };

  if (!clientId && !loading) {
    return <div className="no-data">Select a company to configure its review widget.</div>;
  }

  return (
    <div className="widget-config-page">
      <div className="widget-config-intro">
        <p>
          This widget shows only reviews with the <strong>Show on Site</strong> flag enabled.
          Paste the embed code on any website to display this company&apos;s reviews.
        </p>
      </div>

      <div className="widget-config-toolbar">
        {isAdmin() ? (
          <div>
            <label htmlFor="widget-company">Company</label>
            <select
              id="widget-company"
              className="client-select"
              value={clientId || ''}
              onChange={(e) => setClientId(Number(e.target.value))}
            >
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <strong>{companyName || 'Company widget'}</strong>
          </div>
        )}
        <label className="widget-enabled-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Widget enabled
        </label>
      </div>

      {loading ? (
        <div className="loading">Loading widget settings...</div>
      ) : (
        <div className="widget-config-grid">
          <div>
            <div className="widget-config-card">
              <h3>Appearance</h3>
              <div className="widget-fields">
                <div className="widget-field full">
                  <label htmlFor="widget-title">Title</label>
                  <input
                    id="widget-title"
                    type="text"
                    value={config.title}
                    onChange={(e) => updateConfig('title', e.target.value)}
                    maxLength={120}
                  />
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-layout">Layout</label>
                  <select
                    id="widget-layout"
                    value={config.layout}
                    onChange={(e) => updateConfig('layout', e.target.value as WidgetStyleConfig['layout'])}
                  >
                    <option value="carousel">Carousel</option>
                    <option value="list">List</option>
                    <option value="grid">Grid</option>
                  </select>
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-font">Font</label>
                  <select
                    id="widget-font"
                    value={config.font_family}
                    onChange={(e) => updateConfig('font_family', e.target.value)}
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-max">Max reviews</label>
                  <input
                    id="widget-max"
                    type="number"
                    min={1}
                    max={50}
                    value={config.max_reviews}
                    onChange={(e) => updateConfig('max_reviews', Number(e.target.value) || 1)}
                  />
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-font-size">Font size (px)</label>
                  <input
                    id="widget-font-size"
                    type="number"
                    min={10}
                    max={24}
                    value={config.font_size}
                    onChange={(e) => updateConfig('font_size', Number(e.target.value) || 14)}
                  />
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-radius">Corner radius</label>
                  <input
                    id="widget-radius"
                    type="number"
                    min={0}
                    max={40}
                    value={config.border_radius}
                    onChange={(e) => updateConfig('border_radius', Number(e.target.value) || 0)}
                  />
                </div>
                <div className="widget-field">
                  <label htmlFor="widget-autoplay-interval">Carousel speed (ms)</label>
                  <input
                    id="widget-autoplay-interval"
                    type="number"
                    min={2000}
                    max={20000}
                    step={500}
                    value={config.autoplay_interval}
                    onChange={(e) => updateConfig('autoplay_interval', Number(e.target.value) || 5000)}
                    disabled={config.layout !== 'carousel'}
                  />
                </div>

                {COLOR_FIELDS.map((field) => (
                  <div className="widget-field" key={field.key}>
                    <label htmlFor={`widget-${field.key}`}>{field.label}</label>
                    <div className="widget-color-row">
                      <input
                        type="color"
                        value={String(config[field.key])}
                        onChange={(e) => updateConfig(field.key, e.target.value as never)}
                      />
                      <input
                        id={`widget-${field.key}`}
                        type="text"
                        value={String(config[field.key])}
                        onChange={(e) => updateConfig(field.key, e.target.value as never)}
                      />
                    </div>
                  </div>
                ))}

                <div className="widget-field full">
                  <label>Display options</label>
                  <div className="widget-checks">
                    <label>
                      <input
                        type="checkbox"
                        checked={config.show_average}
                        onChange={(e) => updateConfig('show_average', e.target.checked)}
                      />
                      Show average rating
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={config.show_name}
                        onChange={(e) => updateConfig('show_name', e.target.checked)}
                      />
                      Show reviewer name
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={config.show_rating}
                        onChange={(e) => updateConfig('show_rating', e.target.checked)}
                      />
                      Show star rating
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={config.show_comment}
                        onChange={(e) => updateConfig('show_comment', e.target.checked)}
                      />
                      Show comment
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={config.show_date}
                        onChange={(e) => updateConfig('show_date', e.target.checked)}
                      />
                      Show date
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={config.autoplay}
                        onChange={(e) => updateConfig('autoplay', e.target.checked)}
                        disabled={config.layout !== 'carousel'}
                      />
                      Carousel autoplay
                    </label>
                  </div>
                </div>
              </div>

              <div className="widget-actions">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save widget'}
                </button>
                {message ? <span className={`widget-status ${message.type}`}>{message.text}</span> : null}
              </div>
            </div>

            <div className="widget-config-card widget-embed" style={{ marginTop: 24 }}>
              <h3>Embed code</h3>
              <p className="widget-embed-note">
                Paste this snippet where you want reviews to appear on the company website.
                Each company has its own widget ID.
              </p>
              <pre>{embedCode}</pre>
              <button type="button" className="btn btn-primary" onClick={handleCopy} disabled={!embedCode}>
                {copied ? 'Copied!' : 'Copy embed code'}
              </button>
            </div>
          </div>

          <div className="widget-preview-wrap">
            <div className="widget-config-card">
              <h3>Live preview</h3>
              {reviews.length === 0 ? (
                <p className="widget-embed-note">
                  Showing sample reviews until this company has reviews with &quot;Show on Site&quot; enabled.
                </p>
              ) : null}
              <WidgetPreview
                config={config}
                reviews={reviews.length > 0 ? reviews : SAMPLE_REVIEWS}
                averageRating={reviews.length > 0 ? averageRating : 9.33}
                total={reviews.length > 0 ? total : SAMPLE_REVIEWS.length}
                companyName={companyName}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetConfigPage;
